<script setup lang="ts">
import { Phone } from 'lucide-vue-next'
import type { NavigationConfig } from '~~/shared/types/navigation'
import MobileMenuSection from './MobileMenuSection.vue'

/**
 * Mobile navigation menu (slide-out drawer)
 * Displays all navigation items in an accordion-style layout
 * Updated to work with Hook/Products/Actions structure
 */

// Prevent Vue warning about extraneous attributes on fragment root
defineOptions({
  inheritAttrs: false
})

const attrs = useAttrs()

defineProps<{
  isOpen: boolean
  navigation: NavigationConfig
}>()

const emit = defineEmits<{
  close: []
  openSearch: []
}>()

const { open: openQuoteModal } = useIntentModal()

function handleQuoteClick() {
  emit('close')
  openQuoteModal('free-quote')
}

// Track which accordion sections are open
const openSections = ref<Set<string>>(new Set())

function toggleSection(id: string) {
  if (openSections.value.has(id)) {
    openSections.value.delete(id)
  } else {
    openSections.value.add(id)
  }
  // Trigger reactivity
  openSections.value = new Set(openSections.value)
}

function handleLinkClick() {
  emit('close')
}
</script>

<template>
  <Transition
    enter-active-class="transition-transform duration-300 ease-out"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transition-transform duration-200 ease-in"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <div
      v-if="isOpen"
      v-bind="attrs"
      class="fixed inset-0 top-[84px] z-50 overflow-y-auto bg-white lg:hidden"
    >
      <div class="flex min-h-full flex-col">
        <!-- Navigation sections -->
        <nav class="flex-1 divide-y divide-neutral-100">
          <MobileMenuSection
            v-for="item in navigation.megaMenuItems"
            :key="item.id"
            :item="item"
            :is-open="openSections.has(item.id)"
            @toggle="toggleSection(item.id)"
            @link-click="handleLinkClick"
          />
        </nav>

        <!-- Bottom actions -->
        <div class="border-t border-neutral-200 p-4 pb-8">
          <!-- Phone -->
          <a
            :href="navigation.phone.href"
            class="flex w-full items-center justify-center gap-2 rounded-lg bg-accent-500 px-4 py-3 font-semibold text-white transition-colors hover:bg-accent-600"
            @click="handleLinkClick"
          >
            <Phone class="h-5 w-5" aria-hidden="true" />
            <span>{{ navigation.phone.label }}</span>
          </a>

          <!-- Quote CTA -->
          <button
            type="button"
            class="mt-3 flex w-full items-center justify-center rounded-lg bg-primary-500 px-4 py-3 font-semibold text-white transition-colors hover:bg-primary-600"
            @click="handleQuoteClick"
          >
            {{ navigation.cta.label }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Backdrop -->
  <Transition
    enter-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isOpen"
      class="fixed inset-0 top-[84px] z-40 bg-black/50 lg:hidden"
      aria-hidden="true"
      @click="emit('close')"
    ></div>
  </Transition>
</template>
